import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Upload, message, Space } from 'antd';
import ImgCrop from 'antd-img-crop';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { useHallazgoImagenUpload } from 'hooks/useHallazgoImagenUpload';
import { savePhoto, deletePhoto } from 'utils/offlinePhotosStore';
import { downscaleImage } from 'utils/normalizeImage';

const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const MAX_COUNT = 4;

const validate = (file, totalCount) => {
  if (!ALLOWED_MIMES.includes(file.type)) {
    message.error('Formato no soportado. Usá JPG, PNG o WebP.');
    return false;
  }
  if (file.size > MAX_SIZE_BYTES) {
    message.error(`La foto supera los ${MAX_SIZE_MB} MB.`);
    return false;
  }
  if (totalCount >= MAX_COUNT) {
    message.error(`Máximo ${MAX_COUNT} fotos por hallazgo.`);
    return false;
  }
  return true;
};

// Modo edit (idHallazgo presente): sube al toque (presign→PUT→confirm) y llama onUploaded().
// Modo create (idHallazgo null): acumula pendingFiles; el parent llama ref.uploadPending(idHallazgo).
//
// Props:
//   currentCount — cantidad de fotos YA PERSISTIDAS en la DB (el parent debe pasarla
//                  cuando idHallazgo está presente). En modo edit, es la cantidad de fotos
//                  existentes para que el tope de MAX_COUNT funcione correctamente.
//                  En modo create, déjala en 0 (default); el tope se calcula con pendingFiles.
//
// Modo CONTROLADO (opt-in): si el padre pasa AMBAS props `pendingFiles` (array de {file,url})
//   Y `onPendingFilesChange` (setter), el componente opera sobre el estado del padre en vez
//   del propio. Las blob URLs NO se revocan en unmount en este modo — el padre es dueño
//   del lifecycle; debe revocarlas cuando el Item completo se desmonta.
//   Esto permite que las fotos pendientes sobrevivan al desmonte del uploader (colapso de
//   bloque o cambio de estado) sin perderse en silencio.
const HallazgoFotosUpload = forwardRef(function HallazgoFotosUpload(
  {
    idHallazgo,
    onUploaded,
    disabled,
    currentCount = 0,
    pendingFiles: pendingFilesProp,
    onPendingFilesChange,
    // Metadata opcional para persistir el binario offline (modo create). Si se
    // proveen, cada foto pendiente se guarda en IndexedDB para sobrevivir recargas
    // y el daemon de sync la sube al reconectar. Sin estas props → comportamiento
    // legacy (solo memoria, sin persistencia).
    //
    // Multi-hallazgo: la foto se keyea por `tempId` (identidad cliente de la fila
    // del hallazgo). El daemon resuelve tempId → idHallazgo desde la respuesta del
    // PUT para subirla al hallazgo real.
    mantencionId,
    tempId,
  },
  ref
) {
  // Modo controlado: el padre provee AMBAS props.
  const isControlled = pendingFilesProp !== undefined && typeof onPendingFilesChange === 'function';

  // Estado interno — solo activo en modo NO controlado.
  const [internalPendingFiles, setInternalPendingFiles] = useState([]);

  // Selector unificado de estado y setter.
  const items = isControlled ? pendingFilesProp : internalPendingFiles;
  const setItems = isControlled ? onPendingFilesChange : setInternalPendingFiles;

  const { upload, loading } = useHallazgoImagenUpload();

  // Ref que espeja el estado actual de items para:
  //   1. Revocar en unmount sin stale closure (el cleanup de [] captura la ref, no el valor).
  //   2. Leer items.length en beforeUpload sin incluirlos en las deps del useCallback
  //      (evita recrear el callback en cada foto agregada).
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Revocar todas las URLs al desmontar — SOLO en modo NO controlado.
  // En modo controlado el padre es dueño del lifecycle de las URLs; revocar
  // aquí destruiría blobs que siguen siendo necesarios cuando el uploader
  // se remonta (después de colapsar/reabrir el bloque).
  useEffect(() => {
    if (isControlled) return undefined;
    return () => {
      itemsRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
    };
    // isControlled no cambia durante la vida del componente — la dependencia
    // es estable; se usa para condicionar el cleanup en el efecto de mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref estable para setItems para que beforeUpload no se recree al cambiar items.
  // setInternalPendingFiles es estable por garantía de React; onPendingFilesChange
  // debería serlo también (viene de useState del padre), pero lo wrapeamos por si acaso.
  const setItemsRef = useRef(setItems);
  useEffect(() => {
    setItemsRef.current = setItems;
  }, [setItems]);

  // FIX: tope en modo edit — valida contra fotos persistidas + pendientes acumuladas.
  // En modo create: currentCount=0, totalCount = items.length (leído vía ref, sin dep).
  // En modo edit:   currentCount = fotos en DB (prop del parent), items siempre 0.
  //
  // Se usa forma funcional de setItems (prev => [...prev, item]) para evitar stale
  // closure cuando el callback es reutilizado entre renders sin actualizarse.
  const beforeUpload = useCallback(
    async (file) => {
      const totalCount = currentCount + itemsRef.current.length;
      if (!validate(file, totalCount)) return Upload.LIST_IGNORE;
      // La foto llega ya recortada cuadrada desde ImgCrop; la normalizamos
      // (lado máx. + jpeg comprimido) para que el informe/PDF no pese de más.
      const photo = await downscaleImage(file);
      if (idHallazgo) {
        try {
          await upload(idHallazgo, photo);
          onUploaded?.();
          message.success('Foto agregada');
        } catch (err) {
          message.error(err?.message || 'No se pudo subir la foto');
        }
      } else {
        // FIX: crear la object URL en el event handler, no en el render.
        // Así el lifecycle de la URL es: creada al seleccionar → revocada al
        // desmontar (o tras subir). El useMemo anterior revocaba la URL de la
        // foto anterior en cada render — quitando el blob en uso del navegador.
        const url = URL.createObjectURL(photo);
        // Offline: si hay metadata, persistir el binario en IndexedDB con un
        // localId estable. El binario sobrevive recargas/cierre sin red; el
        // daemon de sync lo sube al reconectar y luego lo borra.
        let localId;
        if (mantencionId != null && tempId != null) {
          localId = uuidv4();
          await savePhoto(localId, {
            blob: photo,
            name: photo.name,
            type: photo.type,
            mantencionId,
            tempId,
          });
        }
        // Forma funcional: siempre recibe el estado actual, nunca el snapshot
        // del closure — previene pérdidas cuando el callback es stale.
        setItemsRef.current((prev) => [...prev, { file: photo, url, localId }]);
      }
      return Upload.LIST_IGNORE;
    },
    [idHallazgo, upload, onUploaded, currentCount, mantencionId, tempId]
  );

  useImperativeHandle(
    ref,
    () => ({
      async uploadPending(newId) {
        if (!itemsRef.current.length) return;
        if (!newId) throw new Error('uploadPending requiere idHallazgo');
        const snapshot = itemsRef.current;
        const toRevoke = snapshot.map(({ url }) => url);
        for (const item of snapshot) {
          // eslint-disable-next-line no-await-in-loop
          await upload(newId, item.file);
          // Subida OK → borrar el binario offline persistido (si lo había).
          if (item.localId) {
            // eslint-disable-next-line no-await-in-loop
            await deletePhoto(item.localId);
          }
        }
        // Revocar las URLs de las fotos que ya se subieron antes de limpiar state.
        // Esto aplica en AMBOS modos: las fotos ya fueron consumidas por el upload.
        toRevoke.forEach((url) => URL.revokeObjectURL(url));
        setItemsRef.current([]);
      },
      hasPending() {
        return itemsRef.current.length > 0;
      },
      pendingCount() {
        return itemsRef.current.length;
      },
    }),
    [upload]
  );

  return (
    <Space wrap>
      {/* FIX: key estable con name+lastModified en vez de índice */}
      {items.map(({ file, url }) => (
        <img
          key={`${file.name}-${file.lastModified}`}
          src={url}
          alt={`pendiente ${file.name}`}
          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }}
        />
      ))}
      {/* Recorte cuadrado en la captura: el técnico ve el recuadro final ("Así se
          verá en el informe") y lo acomoda antes de guardar. quality=1 para no
          recomprimir acá; la compresión la hace downscaleImage en beforeUpload. */}
      <ImgCrop
        aspect={1}
        quality={1}
        cropShape="rect"
        showGrid
        modalTitle="Ajustar recorte"
        modalOk="Usar foto"
        modalCancel="Cancelar"
      >
        {/* SIN `multiple`: antd-img-crop comparte un único modal de recorte; con
            selección múltiple solo se recorta/sube el último archivo y el resto
            se pierde (los onOk.current se pisan). Se agregan de a una. */}
        <Upload
          listType="picture-card"
          showUploadList={false}
          beforeUpload={beforeUpload}
          disabled={disabled || loading}
          accept="image/*"
        >
          <div>
            {loading ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 6, fontSize: 12 }}>{loading ? 'Subiendo…' : 'Foto'}</div>
          </div>
        </Upload>
      </ImgCrop>
    </Space>
  );
});

export default HallazgoFotosUpload;
